/**
 * Support-chat API client — #3104 (#3096-D).
 *
 * Talks to the new `SupportApiStack` HTTP API at `https://api.2hives.ai/v1/support/*`
 * defined in `specs/3096-support-master-v0.1.md`. The widget (sub-card D) only
 * needs the conversation-create + message-send + upload-sign + finalize + close
 * endpoints; WS delivery is handled directly by the widget (see
 * `components/support/SupportChatWidget.tsx`).
 *
 * Reserved-words hygiene (#3154): payloads use `op` not `type`; `agent_role`
 * not `role`; `llm_model` not `model`. The WS frame envelope follows the same
 * rule (`op: "support_message_appended"` etc.).
 *
 * Logged-in-only per founder decision #3096-1 (2026-06-03): every call goes
 * through `adminFetch` which carries the Cognito JWT. No anonymous /
 * Turnstile path is wired in this client — if the founder later reverses the
 * decision, an `anonymousSupportFetch` peer can be added without touching the
 * widget.
 */
import { adminFetch } from "./userApi";

// ── Wire types — match SupportApiStack response shapes ──────────────────────

export type SupportEmbedOrigin = "marketing_site" | "admin_drawer";

export interface CreateConversationResponse {
  conversation_id: string;
  ws_url:          string;
}

export interface SupportAttachment {
  upload_id:   string;
  s3_key:      string;
  mime:        string;
  size:        number;
  scan_status: "pending" | "clean" | "infected" | "error";
}

export interface SupportMessage {
  message_id:  string;
  agent_role:  "user" | "assistant" | "system" | "tool";
  body_md:     string;
  created_at:  string;
  attachments: SupportAttachment[];
}

export interface MessagesPage {
  messages: SupportMessage[];
}

export interface FileSignResponse {
  upload_id:   string;
  post_url:    string;
  post_fields: Record<string, string>;
  s3_key:      string;
}

export interface FileFinalizeResponse {
  scan_status: SupportAttachment["scan_status"];
  ready:       boolean;
}

export const supportChatApi = {
  /**
   * Reserve a new conversation id. Does NOT spawn the support sub-agent —
   * that happens on the first user message per spec §2.2.
   */
  createConversation: (referrerOrigin: SupportEmbedOrigin): Promise<CreateConversationResponse> =>
    adminFetch("POST", "/v1/support/conversations", { referrer_origin: referrerOrigin }),

  /**
   * Send a user message. Backend returns 202 + `message_id`; the assistant
   * reply arrives over WS as `support_message_appended`.
   */
  sendMessage: (
    conversationId: string,
    bodyMd: string,
    attachmentUploadIds: string[] = [],
  ): Promise<{ message_id: string }> =>
    adminFetch("POST", `/v1/support/conversations/${encodeURIComponent(conversationId)}/messages`, {
      body_md:     bodyMd,
      attachments: attachmentUploadIds,
    }),

  /**
   * Polling fallback when the WS connection is unavailable. `since` is the
   * last seen `message_id` (ULIDs sort chronologically); the server returns
   * everything appended after it.
   */
  pollMessages: (conversationId: string, since?: string): Promise<MessagesPage> => {
    const qs = since ? `?since=${encodeURIComponent(since)}` : "";
    return adminFetch("GET", `/v1/support/conversations/${encodeURIComponent(conversationId)}/messages${qs}`);
  },

  /** Step 1 of the upload flow — pre-signed S3 POST policy. */
  signUpload: (
    conversationId: string,
    filename: string,
    mime: string,
    size: number,
  ): Promise<FileSignResponse> =>
    adminFetch(
      "POST",
      `/v1/support/conversations/${encodeURIComponent(conversationId)}/files/sign`,
      { filename, mime, size },
    ),

  /** Step 3 — once the client has PUT the bytes to S3, finalize via id. */
  finalizeUpload: (
    conversationId: string,
    uploadId: string,
  ): Promise<FileFinalizeResponse> =>
    adminFetch(
      "POST",
      `/v1/support/conversations/${encodeURIComponent(conversationId)}/files/finalize`,
      { upload_id: uploadId },
    ),

  /** Explicit user-driven close. Idempotent. */
  closeConversation: (conversationId: string, reason?: string): Promise<{ closed: boolean }> =>
    adminFetch(
      "POST",
      `/v1/support/conversations/${encodeURIComponent(conversationId)}/close`,
      reason ? { reason } : {},
    ),
};

// ── localStorage cursor ─────────────────────────────────────────────────────

/**
 * Conversation cursor — persists the active `conversation_id` + the last seen
 * `message_id` across reloads + tabs. The spec calls for a 14-day TTL; the
 * widget enforces this when reading.
 */
export interface SupportCursor {
  conversation_id: string;
  last_message_id: string | null;
  created_at:      number;   // epoch ms
  updated_at:      number;   // epoch ms
}

export const SUPPORT_CURSOR_KEY = "support_chat_cursor_v1";
export const SUPPORT_CURSOR_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export function readSupportCursor(now: number = Date.now()): SupportCursor | null {
  try {
    const raw = localStorage.getItem(SUPPORT_CURSOR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SupportCursor;
    if (typeof parsed?.conversation_id !== "string") return null;
    if (typeof parsed.created_at !== "number") return null;
    if (now - parsed.created_at > SUPPORT_CURSOR_TTL_MS) {
      // Expired — clean up so the next open creates a fresh conversation.
      localStorage.removeItem(SUPPORT_CURSOR_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeSupportCursor(cursor: SupportCursor): void {
  try {
    localStorage.setItem(SUPPORT_CURSOR_KEY, JSON.stringify(cursor));
  } catch {
    /* localStorage may be disabled (private mode); the widget still works,
       it just won't survive a reload. */
  }
}

export function clearSupportCursor(): void {
  try { localStorage.removeItem(SUPPORT_CURSOR_KEY); } catch { /* ignore */ }
}
